import { computed, Directive, inject, input } from '@angular/core';

import { urlUtils } from '@blaze-shared/platform-util';
import { type FileResult } from '@blaze-shared/uploader';

import { FileService } from '../../services/file.service';

/**
 * Turns the host anchor into an "open or download" link for an uploaded file.
 *
 * Non-secure files are served straight from `href` by the browser. Secure-vault files have no
 * publicly reachable URL, so the activation is intercepted and brokered by {@link FileService};
 * because those anchors carry no `href`, the directive restores the link role and keyboard
 * focusability the browser would otherwise withhold.
 *
 * `isDisabled` makes the link inert, for hosts that must yield their clicks to something else —
 * an editor canvas selecting the element, say. It is deliberately per-host rather than a blanket
 * editor rule: an element can keep one live link while suppressing another.
 */
@Directive({
  selector: 'a[blazeFileDownloadLink]',
  host: {
    '[attr.href]': 'href()',
    '[attr.download]': 'file().filename',
    '[attr.role]': 'href() ? null : "link"',
    '[attr.tabindex]': 'tabIndex()',
    // A secure file's anchor has no href, so the pointer cursor cannot be left to the browser.
    '[class.cursor-pointer]': '!isDisabled()',
    '[class.pointer-events-none]': 'isDisabled()',
    '(click)': 'openFile($event)',
    '(keydown.enter)': 'openFile($event)',
    target: '_blank',
    rel: 'noopener noreferrer',
  },
})
export class FileDownloadLinkDirective {
  private readonly fileService = inject(FileService);

  readonly file = input.required<FileResult>({ alias: 'blazeFileDownloadLink' });
  readonly isSecure = input(false);
  readonly isDisabled = input(false);

  /** `null` — rather than an empty string — keeps the attribute off anchors with nothing to open. */
  protected readonly href = computed(
    () => urlUtils.getFileUrlHref(this.file().url, this.isSecure()) || null,
  );

  protected readonly tabIndex = computed(() => {
    if (this.isDisabled()) {
      return -1;
    }
    return this.href() ? null : 0;
  });

  protected openFile(event: Event): void {
    const { url } = this.file();

    if (!url) {
      // Nothing to open — stop the empty href from navigating to the current page.
      event.preventDefault();
      return;
    }

    if (!this.isSecure()) {
      // Handled by the href's default behaviour.
      return;
    }

    event.preventDefault();
    this.fileService.downloadSecureFile(url);
  }
}
